<template>
  <div>
    <div class="flex flex-row items-center space-x-10">
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        :label="$t('search')"
        hide-details
        class="mb-4"
      />
    </div>
    <v-alert
      type="note"
      outlined
      border="left"
      color="grey-darken-1"
      icon="mdi-information-outline"
      class="mb-3"
    >
      <p class="ma-0"><strong>Note:</strong> {{ $t('asset.dicom.study.study-info') }}</p>
    </v-alert>
    <v-data-table
      :headers="getHeaders"
      :items="dicomStudies"
      :search="search"
      class="elevation-1 certifai-table"
      :loading="loading"
      :items-per-page="5"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'mdi-arrow-collapse-left',
        lastIcon: 'mdi-arrow-collapse-right',
        prevIcon: 'mdi-arrow-left',
        nextIcon: 'mdi-arrow-right',
        disableItemsPerPage: false,
        itemsPerPageText: getDicomStudiesItemsPerPageText,
        showCurrentPage: true,
      }"
      must-sort
      :sort-desc="true"
    >
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="g-action-btn g-action-view"
              v-on="on"
              v-bind="attrs"
              @click="enablePreviewStudyDialog(item)"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('preview') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              class="g-action-btn g-action-send"
              v-on="on"
              v-bind="attrs"
              @click="enableOnboardDialog(item)"
            >
              <v-icon small>mdi-send</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('asset.dicom.study.onboard-upload') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <v-dialog v-if="study" v-model="previewStudyClicked" max-width="900">
      <v-card>
        <v-card-title class="headline">{{ $t('asset.dicom.study.title') }}</v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.dicom.study.patientMainDicomTags.patientName') }}: </strong>
                  {{ study.patientMainDicomTags.PatientName }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.dicom.study.patientMainDicomTags.patientSex') }}: </strong>
                  {{ study.patientMainDicomTags.PatientSex }}
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.dicom.study.isStable') }}: </strong>{{ study.isStable }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.dicom.study.lastUpdate') }}: </strong>
                  {{ study.lastUpdate }}
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.dicom.study.mainDicomTags.studyDate') }}: </strong>
                  {{ formatDateString(study.mainDicomTags.StudyDate) }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div>
                  <strong>{{ $t('asset.dicom.study.mainDicomTags.studyId') }}: </strong>
                  {{ study.mainDicomTags.StudyID }}
                </div>
              </v-col>
            </v-row>
          </v-container>
          <v-text-field
            v-model="searchInstance"
            append-icon="mdi-magnify"
            :label="$t('search')"
            hide-details
            class="mb-4"
          />
          <v-data-table
            :headers="getInstanceHeaders"
            :items="study.instances"
            :search="searchInstance"
            class="elevation-1 certifai-table"
            :loading="loading"
            :items-per-page="5"
            :footer-props="{
              showFirstLastPage: true,
              firstIcon: 'mdi-arrow-collapse-left',
              lastIcon: 'mdi-arrow-collapse-right',
              prevIcon: 'mdi-arrow-left',
              nextIcon: 'mdi-arrow-right',
              disableItemsPerPage: false,
              itemsPerPageText: getInstanceItemsPerPageText,
              showCurrentPage: true,
            }"
            must-sort
            :sort-desc="true"
          >
            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="g-action-btn g-action-view"
                    v-on="on"
                    v-bind="attrs"
                    @click="enablePreviewInstanceDialog(item)"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('preview') }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
          <v-dialog v-if="previewInstanceItem" v-model="previewInstanceClicked" max-width="900">
            <v-card>
              <v-card-title class="headline">{{
                $t('asset.dicom.instance.simplified-tags.title')
              }}</v-card-title>
              <v-text-field
                v-model="searchSimplifiedTags"
                append-icon="mdi-magnify"
                :label="$t('search')"
                hide-details
                class="mb-4 px-10"
              />
              <v-card-text>
                <v-data-table
                  :headers="getInstanceSimplifiedTagsHeaders"
                  :items="dicomSimplifiedTags"
                  :search="searchSimplifiedTags"
                  class="elevation-1 certifai-table"
                  :loading="loading"
                  :items-per-page="5"
                  :footer-props="{
                    showFirstLastPage: true,
                    firstIcon: 'mdi-arrow-collapse-left',
                    lastIcon: 'mdi-arrow-collapse-right',
                    prevIcon: 'mdi-arrow-left',
                    nextIcon: 'mdi-arrow-right',
                    disableItemsPerPage: false,
                    itemsPerPageText: getInstanceSimplifiedTagsItemsPerPageText,
                    showCurrentPage: true,
                  }"
                  must-sort
                  :sort-desc="true"
                >
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          class="g-action-btn g-action-view"
                          v-on="on"
                          v-bind="attrs"
                          @click="enablePreviewSimplifiedTagsDialog(item)"
                        >
                          <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('preview') }}</span>
                    </v-tooltip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-dialog>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-if="onboardItem" v-model="onboardClicked" max-width="900">
      <v-card>
        <v-card-title>
          {{ $t('asset.dicom.study.onboard-upload') }}
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="py-4 px-6">
          <p class="subtitle-1 mb-4">{{ $t('asset.dicom.study.onboard-checkbox') }}</p>

          <v-row class="align-center mb-3">
            <v-col cols="auto" class="px-2">
              <div class="font-weight-medium">
                <strong>{{ $t('asset.dicom.study.patientMainDicomTags.patientName') }}: </strong>
                {{ onboardItem.patientMainDicomTags.PatientName }}
              </div>
            </v-col>
            <v-col cols="auto">
              <v-checkbox v-model="nameChecked" class="mt-0" color="primary">
                <v-icon left>mdi-account</v-icon>
              </v-checkbox>
            </v-col>
          </v-row>

          <v-row class="align-center mb-3">
            <v-col cols="auto" class="px-2">
              <div class="font-weight-medium">
                <strong>{{ $t('asset.dicom.study.patientMainDicomTags.patientSex') }}: </strong>
                {{ onboardItem.patientMainDicomTags.PatientSex }}
              </div>
            </v-col>
            <v-col cols="auto">
              <v-checkbox v-model="patientSexChecked" class="mt-0" color="primary">
                <v-icon left>mdi-gender-male-female</v-icon>
              </v-checkbox>
            </v-col>
          </v-row>

          <v-row class="align-center mb-3">
            <v-col cols="auto" class="px-2">
              <div class="font-weight-medium">
                <strong>{{ $t('asset.dicom.study.patientMainDicomTags.birthDate') }}: </strong>
                {{ formatDateString(onboardItem.patientMainDicomTags.PatientBirthDate) }}
              </div>
            </v-col>
            <v-col cols="auto">
              <v-checkbox v-model="birthDateChecked" class="mt-0" color="primary">
                <v-icon left>mdi-calendar</v-icon>
              </v-checkbox>
            </v-col>
          </v-row>

          <p class="mt-4">{{ $t('asset.dicom.study.onboard-message') }}</p>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-4 px-6">
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="onboardClicked = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn color="red darken-1" :loading="this.loading" text @click="onboardDicomStudy">
            {{ $t('continue') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import util from '@/mixins/util';
import { Dicom } from '@/modules/asset/api';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'AssetDicomUpload',
  mixins: [util],
  props: {
    server: {
      type: Object,
      required: true,
    },
  },
  computed: {
    getSimplifiedTagsItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('asset.dicom.simplified-tags.table-footer');
    },
    getInstanceSimplifiedTagsItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('asset.dicom.instance.simplified-tags.table-footer');
    },
    getDicomStudiesItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('asset.dicom.study.table-footer');
    },
    getHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('asset.dicom.study.id'),
          value: 'id',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '90%',
        },
        {
          text: t('asset.headers.actions'),
          value: 'actions',
          align: 'center',
          sortable: false,
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
    getInstanceSimplifiedTagsHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('asset.dicom.instance.simplified-tags.key'),
          value: 'key',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '50%',
        },
        {
          text: t('asset.dicom.instance.simplified-tags.value'),
          value: 'value',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '50%',
        },
      ];
    },
    getInstanceItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('asset.dicom.instance.table-footer');
    },
    getInstanceHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('asset.dicom.instance.id'),
          value: 'id',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '90%',
        },
        {
          text: t('asset.headers.actions'),
          value: 'actions',
          align: 'center',
          sortable: false,
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
    getSimplifiedTagsHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('asset.dicom.simplified-tags.key'),
          value: 'key',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '50%',
        },
        {
          text: t('asset.dicom.simplified-tags.value'),
          value: 'value',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '50%',
        },
      ];
    },
  },
  data() {
    return {
      loading: false,
      dicomStudies: [],
      search: null,
      searchInstance: null,
      dicomSimplifiedTags: [],
      searchSimplifiedTags: null,
      previewInstanceItem: null,
      previewInstanceClicked: false,
      study: null,
      previewStudyClicked: false,
      onboardItem: null,
      onboardClicked: false,
      nameChecked: false,
      patientSexChecked: false,
      birthDateChecked: false,
    };
  },
  created() {
    this.$emit('loading', true);
    this.loading = true;
    this.fetchDicomStudies(this.server.url, this.server.username, this.server.password);
    this.$emit('loading', false);
    this.loading = false;
  },
  methods: {
    async enablePreviewInstanceDialog(item) {
      this.previewInstanceItem = item;

      var rawDicomSimplifiedTags = await this.fetchInstanceSimplifiedTags(item.id);

      this.dicomSimplifiedTags = rawDicomSimplifiedTags;

      this.previewInstanceClicked = true;
    },
    async fetchDicomStudies(url, username, password) {
      var payload = {
        url: url,
        username: username,
        password: password,
      };

      try {
        const { data: rawDicomStudyIdList } = await Dicom.studies(payload);
        if (rawDicomStudyIdList && rawDicomStudyIdList.length > 0) {
          const rawDicomStudyArray = rawDicomStudyIdList.map(idx => ({ id: idx }));
          this.dicomStudies = rawDicomStudyArray;
        }
      } catch (error) {
        console.error('Error fetching DICOM studies:', error);
      }
    },
    async enablePreviewStudyDialog(item) {
      var payload = {
        url: this.server.url,
        username: this.server.username,
        password: this.server.password,
      };
      console.log(payload);
      var rawStudyInformation = await this.fetchStudyInformation(item.id, payload);
      this.study = rawStudyInformation;

      this.previewStudyClicked = true;
    },
    async fetchInstanceSimplifiedTags(dicomInstanceId) {
      var payload = {
        url: this.server.url,
        username: this.server.username,
        password: this.server.password,
      };
      const { data: rawSimplifiedTags } = await Dicom.simplifiedTags(dicomInstanceId, payload);

      return rawSimplifiedTags;
    },
    async fetchStudyInformation(dicomStudyId, request) {
      const { data: rawStudyInformation } = await Dicom.studyInformation(dicomStudyId, request);

      return rawStudyInformation;
    },
    async enableOnboardDialog(item) {
      var payload = {
        url: this.server.url,
        username: this.server.username,
        password: this.server.password,
      };
      console.log(payload);
      var rawStudyInformation = await this.fetchStudyInformation(item.id, payload);
      // this.study = rawStudyInformation;
      this.onboardItem = rawStudyInformation;
      console.log(this.onboardItem);
      this.onboardClicked = true;
    },
    async onboardDicomStudy() {
      var payload = {
        dicomDto: {
          url: this.server.url,
          username: this.server.username,
          password: this.server.password,
        },
        anonymizationSelectionDto: {
          anonName: this.nameChecked,
          anonBirthDate: this.birthDateChecked,
          anonSex: this.patientSexChecked,
        },
      };
      console.log('payload');
      console.log(payload);
      console.log('Sending request to /onboard the dicom study: ', this.onboardItem.id);
      this.loading = true;

      await Dicom.onboardStudy(this.onboardItem.id, payload)
        .then(() => {
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('asset.dicom.upload-success'),
            color: 'success',
          });
        })
        .catch(error => {
          if (error.request && error.response.status === 400) {
            this.errorSummary = this.$i18n.t('errorList.error-400');
          }
        })
        .finally(() => {
          this.loading = false;
          this.onboardClicked = false;

          this.$router.push({
            name: 'Assets',
          });
        });
      this.onboardClicked = false;
    },
  },
};
</script>
